import React from 'react';
import { useAppDispatch } from "../../../../../../../../app/hooks";
import {
    FieldOptionDTO,
    UpdateBotToolSettingsRequest
} from "../../../../../../../../api/generated";
import {
    updateConditionGroup,
    deleteConditionGroup,
    addCondition,
    UpdateConditionGroupRequestUI
} from "../../../toolSettingsSlice";
import { DeleteButton } from "../../../../../../../elements/deleteButton/DeleteButton";
import { CustomSwitchField } from "../../../../../../../elements/customSwitchField/CustomSwitchField";
import { Condition } from "./Condition";
import { ValidationResult } from "../../../../../../../elements/validator/component/ValidationComponent";
import { NotEmptyListValidator } from "../../../../../../../elements/validator/Validator";
import { DependantValidationComponent } from "../../../../../../../elements/validator/dependant/DependantValidationComponent";
import styles from "./ConditionGroup.module.css";

interface ConditionGroupProps {
    group: UpdateConditionGroupRequestUI;
    groupIndex: number;
    sectionIndex: number;
    fieldOptions: FieldOptionDTO[];
    disabled?: boolean;
    updateValidationStatus: (status: ValidationResult<UpdateBotToolSettingsRequest>) => void;
    getMessageWrapper: (f: string) => string;
}

export const ConditionGroup: React.FC<ConditionGroupProps> = ({
    group,
    groupIndex,
    sectionIndex,
    fieldOptions,
    disabled,
    updateValidationStatus,
    getMessageWrapper
}) => {
    const dispatch = useAppDispatch();

    const handleToggleMatch = (isAllMatch: boolean) => {
        dispatch(updateConditionGroup({
            sectionIndex,
            groupIndex,
            group: { ...group, isAllMatch }
        }));
    };

    const handleAddCondition = () => {
        dispatch(addCondition({ sectionIndex, groupIndex }));
    };

    const handleDeleteGroup = () => {
        dispatch(deleteConditionGroup({ sectionIndex, groupIndex }));
    };

    const conditions = group.conditions || [];

    return (
        <div className={styles.group}>
            <div className={styles.header}>
                <CustomSwitchField
                    textOnTrue="AND"
                    textOnFalse="OR"
                    value={group.isAllMatch}
                    toggleValue={handleToggleMatch}
                    disabled={disabled}
                />

                <DeleteButton
                    disabled={disabled}
                    handleClick={handleDeleteGroup}
                />
            </div>

            <DependantValidationComponent<any[], UpdateBotToolSettingsRequest>
                selector={(state) => {
                    const modifierRules = state.toolSettings.updateRequest.modifierRules;
                    const section = modifierRules?.ruleSections?.[sectionIndex];
                    const group = section?.conditionGroups?.[groupIndex];
                    return group?.conditions || [];
                }}
                anchorValueSelector={(state) => state.toolSettings.updateRequest.modifierRules?.isActive}
                fieldName={`modifierRules.ruleSections[${sectionIndex}].conditionGroups[${groupIndex}].conditions` as keyof UpdateBotToolSettingsRequest}
                validator={NotEmptyListValidator}
                updateValidationStatus={updateValidationStatus}
                onChange={() => {}}
                label="Conditions"
                required={true}
                disabled={disabled}
                child={(label, value, onChange, required) => (
                    <div className={styles.conditions}>
                        {conditions.map((condition, conditionIndex) => (
                            <React.Fragment key={conditionIndex}>
                                <Condition
                                    condition={condition}
                                    conditionIndex={conditionIndex}
                                    groupIndex={groupIndex}
                                    sectionIndex={sectionIndex}
                                    fieldOptions={fieldOptions}
                                    disabled={disabled}
                                    updateValidationStatus={updateValidationStatus}
                                    getMessageWrapper={getMessageWrapper}
                                />
                                {conditionIndex < conditions.length - 1 && (
                                    <div className={styles.logicalOperator}>
                                        {group.isAllMatch ? 'AND' : 'OR'}
                                    </div>
                                )}
                            </React.Fragment>
                        ))}

                        <button
                            className={styles.addConditionButton}
                            onClick={handleAddCondition}
                            disabled={disabled}
                        >
                            + Add Condition
                        </button>
                    </div>
                )}
            />
        </div>
    );
};

